import * as React from "react";

import { SidecarHttpClient } from "../lib/sidecar-http-client";
import { SidecarWsClient } from "../lib/sidecar-ws-client";

/**
 * Local, typing-only re-declarations of the wire shapes this page needs —
 * mirrors `apps/desktop`'s own `lib/timer/types.ts`/`lib/events/types.ts`
 * convention (never importing server packages directly), the same one
 * `routes/control.tsx` already established for this app.
 */
type TimerStatus = "idle" | "running" | "paused" | "overtime" | "stopped";
type TimerType = "countdown" | "countup" | "clock" | "interval" | "session" | "break";

interface TimerState {
  readonly itemId: string | null;
  readonly type: TimerType;
  readonly status: TimerStatus;
  readonly durationMs: number;
  readonly anchorWallMs: number;
  readonly elapsedAtAnchorMs: number;
  readonly targetWallMs?: number;
  readonly seq: number;
}

export interface ScheduleItemDto {
  readonly id: string;
  readonly title: string;
  readonly speaker: string | null;
  readonly durationMs: number;
}

interface ActiveEventDto {
  readonly id: string;
  readonly name: string;
  readonly items: ScheduleItemDto[];
}

export interface CountdownColorTiers {
  readonly countdownTier1RedAtMs: number;
  readonly countdownTier2RedAtMs: number;
  readonly countdownTier3YellowAtMs: number;
  readonly countdownTier3RedAtMs: number;
  readonly countdownTier4YellowAtMs: number;
  readonly countdownTier4RedAtMs: number;
}

export const DEFAULT_COUNTDOWN_COLOR_TIERS: CountdownColorTiers = {
  countdownTier1RedAtMs: 60_000,
  countdownTier2RedAtMs: 120_000,
  countdownTier3YellowAtMs: 300_000,
  countdownTier3RedAtMs: 120_000,
  countdownTier4YellowAtMs: 600_000,
  countdownTier4RedAtMs: 300_000,
};

export type MessageDisplayStyle = "fullscreen" | "lower";

interface MessageState {
  readonly text: string;
  readonly flash: boolean;
  readonly style: MessageDisplayStyle;
}

export interface LiveExtendedDisplayData {
  readonly eventName: string | null;
  readonly currentItem: ScheduleItemDto | null;
  readonly nextItem: ScheduleItemDto | null;
  /** Every item from `nextItem` onward (up to 6) — for a theme's `queue` layer; every other consumer can ignore it. */
  readonly upcomingItems: readonly { title: string; durationMs: number }[];
  readonly message: MessageState | null;
  readonly blackout: boolean;
  readonly now: Date;
  readonly remainingSeconds: number;
  readonly elapsedSeconds: number;
  readonly totalSeconds: number;
  readonly progressPercent: number;
  readonly statusLabel: string;
  readonly displayMode: "clock" | "timer";
  readonly countdownColorTiers: CountdownColorTiers;
  /** A genuine, untimed count-up (`countup`/`session`-type, no duration set) — `remainingSeconds` has no meaningful value for one of these (`computeRemainingMs` reports `Infinity`), so a consumer should show `elapsedSeconds` instead and skip countdown-zone coloring entirely. Mirrors `apps/desktop`'s own `useLiveTimerDisplayData`'s identically-named field. */
  readonly isUntimedCountUp: boolean;
}

const STATUS_LABELS: Record<TimerStatus, string> = {
  idle: "IDLE",
  running: "RUNNING",
  paused: "PAUSED",
  overtime: "OVERTIME",
  stopped: "STOPPED",
};

function computeRemainingMs(state: TimerState, skewMs: number, nowMs: number): number {
  const correctedNowMs = nowMs + skewMs;
  if (state.type === "break") return (state.targetWallMs ?? correctedNowMs) - correctedNowMs;
  if (state.type === "clock") return 0;
  if ((state.type === "countup" || state.type === "session") && state.durationMs <= 0) {
    return Number.POSITIVE_INFINITY;
  }
  const elapsedMs =
    state.status === "running" || state.status === "overtime"
      ? state.elapsedAtAnchorMs + (correctedNowMs - state.anchorWallMs)
      : state.elapsedAtAnchorMs;
  return state.durationMs - elapsedMs;
}

/**
 * Elapsed time since the anchor, wall-clock/skew-corrected — mirrors
 * `apps/desktop`'s own `lib/timer/timer-math.ts`'s `computeElapsedMs`.
 * `computeRemainingMs` reports `Infinity` for an untimed count-up/session
 * (no target to count down to), so a mobile/Browser Display rendering one
 * needs this instead: how long it's actually been running.
 */
function computeElapsedMs(state: TimerState, skewMs: number, nowMs: number): number {
  const correctedNowMs = nowMs + skewMs;
  return state.status === "running" || state.status === "overtime"
    ? state.elapsedAtAnchorMs + (correctedNowMs - state.anchorWallMs)
    : state.elapsedAtAnchorMs;
}

/** Mirrors `apps/desktop`'s `lib/timer/timer-math.ts`'s `computeProgressPercent` — unused visually today (`ExtendedDisplayView`'s own doc comment: not yet rendered there either), kept for prop-shape parity. */
function computeProgressPercent(durationMs: number, remainingMs: number): number {
  if (!Number.isFinite(remainingMs) || durationMs <= 0) return 0;
  const elapsed = durationMs - remainingMs;
  return Math.min(100, Math.max(0, (elapsed / durationMs) * 100));
}

/**
 * The web-client analogue of `apps/desktop`'s own `useLiveTimerDisplayData`
 * — same live data (active event's current/next item, timer state, the
 * operator message, blackout, a ticking wall clock), but sourced from a
 * direct `SidecarWsClient` connection instead of Tauri's `emit`/`listen`
 * IPC (this app has no Tauri process to relay events from at all). Follows
 * `routes/control.tsx`'s own already-established WS data-fetching pattern
 * verbatim: `GET /api/v1/events/active` + `GET /api/v1/timer` on mount, a
 * `SidecarWsClient` subscribed to `["timer","outputs","message","schedule"]`,
 * refetching the active event on `event:activated`/`schedule:changed`/
 * `item:changed` rather than trying to patch it in place.
 */
export function useLiveExtendedDisplayData(
  token: string | null,
  outputId?: string,
): LiveExtendedDisplayData {
  const [event, setEvent] = React.useState<ActiveEventDto | null>(null);
  const [state, setState] = React.useState<TimerState | null>(null);
  const [blackout, setBlackout] = React.useState(false);
  const [message, setMessage] = React.useState<MessageState | null>(null);
  const [countdownColorTiers, setCountdownColorTiers] = React.useState<CountdownColorTiers>(
    DEFAULT_COUNTDOWN_COLOR_TIERS,
  );
  const [now, setNow] = React.useState(() => new Date());
  const skewMsRef = React.useRef(0);

  // `useMemo`/`useEffect` below key off `token` (a stable string) rather
  // than a caller-supplied closure — the earlier version accepted a
  // `() => string | null` getter directly as the dependency, which a
  // naive call site (`() => session.token` written inline at the call
  // site) recreates on every render, defeating the memoization entirely:
  // `http`/the effect below would recompute every render, each pass
  // firing a fresh round of HTTP requests and a new WS connection before
  // the previous one had even settled. Building the closure once, here,
  // from a primitive dependency is what `routes/control.tsx`'s own
  // `useMemo(() => new SidecarHttpClient(() => session.token), [session.token])`
  // already relies on — mirrored exactly.
  const http = React.useMemo(() => new SidecarHttpClient(() => token), [token]);

  const fetchEvent = React.useCallback(async () => {
    try {
      const active = await http.request<ActiveEventDto | undefined>("GET", "/api/v1/events/active");
      setEvent(active ?? null);
    } catch {
      // Transient — the next WS-driven refetch retries naturally.
    }
  }, [http]);

  React.useEffect(() => {
    void fetchEvent();

    void http
      .request<{ timerId: string; state: TimerState }>("GET", "/api/v1/timer")
      .then((r) => setState(r.state))
      .catch(() => undefined);

    void http
      .request<Array<{ blackout: boolean }>>("GET", "/api/v1/outputs")
      .then((outputs) => setBlackout(outputs.some((o) => o.blackout)))
      .catch(() => undefined);

    void http
      .request<CountdownColorTiers>("GET", "/api/v1/display/extended/countdown-color-tiers")
      .then((tiers) => setCountdownColorTiers(tiers))
      .catch(() => undefined);

    // Seeds whatever message is already active — without this, opening a
    // mirrored Browser Display while a message is up would show nothing
    // until the operator sends a new one (`message:set`/`message:cleared`
    // below only ever fire on the *next* change).
    void http
      .request<{ message: MessageState | null }>("GET", "/api/v1/message")
      .then((r) => setMessage(r.message))
      .catch(() => undefined);

    const ws = new SidecarWsClient(
      () => token,
      ["timer", "outputs", "message", "schedule"],
      outputId,
    );
    ws.start();

    const unsubscribers = [
      ws.on("timer:state", (msg) => setState(msg.state as unknown as TimerState)),
      ws.on("output:blackout", (msg) => {
        if (msg.outputId === "all") setBlackout(Boolean(msg.on));
      }),
      ws.on("message:set", (msg) =>
        setMessage({
          text: String(msg.text ?? ""),
          flash: Boolean(msg.flash),
          style: msg.style === "lower" ? "lower" : "fullscreen",
        }),
      ),
      ws.on("message:cleared", () => setMessage(null)),
      ws.on("heartbeat", (msg) => {
        if (typeof msg.serverTime === "number") skewMsRef.current = msg.serverTime - Date.now();
      }),
      ...["event:activated", "schedule:changed", "item:changed"].map((type) =>
        ws.on(type, () => void fetchEvent()),
      ),
    ];

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      ws.stop();
    };
  }, [http, fetchEvent, token, outputId]);

  React.useEffect(() => {
    // 250ms, not 1s — the mobile Controller (`routes/control.tsx`) and the
    // desktop Extended Display (`useNowTick`'s own default) both recompute
    // the countdown four times a second, so a 1s tick here left this
    // surface visibly trailing them by up to a full second at the moment
    // each one crossed a second boundary.
    const id = setInterval(() => setNow(new Date()), 250);
    return () => clearInterval(id);
  }, []);

  const items = event?.items ?? [];
  const currentIndex = state ? items.findIndex((item) => item.id === state.itemId) : -1;
  const currentItem = currentIndex >= 0 ? (items[currentIndex] ?? null) : null;
  const nextItem = currentIndex >= 0 ? (items[currentIndex + 1] ?? null) : null;
  // Starts at the *same* item `nextItem` points to (not one past it) — a
  // theme's `queue` layer renders index 0 as its own "up next" entry and
  // everything after as the plain queue list.
  const upcomingItems =
    currentIndex >= 0
      ? items
          .slice(currentIndex + 1, currentIndex + 7)
          .map((item) => ({ title: item.title, durationMs: item.durationMs }))
      : [];

  const remainingMs = state ? computeRemainingMs(state, skewMsRef.current, now.getTime()) : 0;
  const totalMs = state?.durationMs ?? 0;
  // Was `totalMs - remainingMs`, guarded to 0 whenever `remainingMs` is
  // `Infinity` (the untimed count-up/session case) — that guard meant an
  // untimed count-up's `elapsedSeconds` reported 0 forever too, not just
  // `remainingSeconds`, so a mobile/Browser Display viewing one showed a
  // frozen "00:00" the whole time instead of actually counting up.
  // `computeElapsedMs` derives elapsed directly from the anchor instead of
  // subtracting from `remainingMs`, so it's correct for every timer type.
  const elapsedMs = state ? computeElapsedMs(state, skewMsRef.current, now.getTime()) : 0;
  const isUntimedCountUp =
    !!state && (state.type === "countup" || state.type === "session") && state.durationMs <= 0;

  return {
    eventName: event?.name ?? null,
    currentItem,
    nextItem,
    upcomingItems,
    message,
    blackout,
    now,
    // `Math.trunc`, not `Math.round` — matches `formatDuration`'s own
    // `Math.floor(Math.abs(ms) / 1000)` (the shared formatter the mobile
    // Controller and every desktop operator surface use), so the same
    // underlying millisecond value renders as the same digits everywhere.
    // `Math.round` here made this surface flip its second up to ~500ms
    // before the others did.
    remainingSeconds: Math.trunc((Number.isFinite(remainingMs) ? remainingMs : 0) / 1000),
    elapsedSeconds: Math.trunc(elapsedMs / 1000),
    totalSeconds: Math.trunc(totalMs / 1000),
    progressPercent: computeProgressPercent(totalMs, remainingMs),
    statusLabel: state ? STATUS_LABELS[state.status] : "IDLE",
    // A live `timerType: "clock"` item (`computeRemainingMs`'s own `if
    // (state.type === "clock") return 0` — it has no countdown, it always
    // shows the current time) routes to the same "clock" layout as no
    // active item at all: `ExtendedDisplayView`'s `displayMode === "clock"`
    // branch already renders the real wall clock via `formatDisplayTime`,
    // not a frozen "0:00", so this was previously the one live timer type
    // never actually wired up on this display, mirroring `apps/desktop`'s
    // own analogous fix (`timer-display.tsx`'s `displayMode` calc).
    displayMode: currentItem && state?.type !== "clock" ? "timer" : "clock",
    countdownColorTiers,
    isUntimedCountUp,
  };
}
